
import React, { useState, useMemo, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, ScatterChart, Scatter, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Calculator, TrendingUp, DollarSign, BarChart2, Clipboard, AlertTriangle } from 'lucide-react';

// =========================
// Enhanced Elasticity Model Functions
// =========================
function getVolumeChange(priceDiff, elasticityFactor = 1.0, competitionIntensity = 1.0) {
  // More sophisticated model with elasticity factor and competition intensity
  const linearTerm = -1 * (priceDiff * 40 * elasticityFactor);
  const quadraticTerm = -(priceDiff * priceDiff * 20 * competitionIntensity);
  return Number((linearTerm + quadraticTerm).toFixed(1));
}

// Calculate profit implications based on price and volume changes
function calculateProfitImpact(baseVolume, basePrice, newPrice, baseCost, volumeChangePercent) {
  const oldProfit = baseVolume * (basePrice - baseCost);
  const newVolume = baseVolume * (1 + volumeChangePercent / 100);
  const newProfit = newVolume * (newPrice - baseCost);
  
  return {
    volumeChange: newVolume - baseVolume,
    profitChange: newProfit - oldProfit,
    profitPercent: ((newProfit - oldProfit) / oldProfit) * 100,
    newVolume,
    newProfit
  };
}

// Calculate cross-price elasticity effects (how changes in one fuel affect another)
function getCrossPriceEffect(primaryFuelPriceDiff, crossElasticityFactor = 0.3) {
  // If diesel price increases, some customers might switch to gasohol
  return -1 * primaryFuelPriceDiff * crossElasticityFactor * 15;
}

// Generate price-volume data points from historical data pattern
function generatePriceVolumeDataPoints() {
  // Simulated historical price-volume relationship data points with some randomness
  const data = [];
  for (let price = 30.8; price <= 32.0; price += 0.1) {
    // Add some randomness to simulate real-world data scatter
    const baseVolume = 10000 - (price - 30.8) * 1200;
    const randomFactor = Math.random() * 600 - 300; // +/- 300 random variation
    data.push({
      price: price.toFixed(2),
      volume: Math.max(baseVolume + randomFactor, 0).toFixed(0),
    });
  }
  return data;
}

// Calculate optimal price point for maximum profit
function findOptimalPrice(baseVolume, baseCost, priceRange, elasticityFactor, competitionIntensity) {
  let maxProfit = 0;
  let optimalPrice = 0;
  
  for (let price = priceRange.min; price <= priceRange.max; price += 0.01) {
    // For each potential price point, calculate estimated volume and profit
    const priceDiff = price - priceRange.competitor;
    const volumeChangePercent = getVolumeChange(priceDiff, elasticityFactor, competitionIntensity);
    const volume = baseVolume * (1 + volumeChangePercent / 100);
    const profit = volume * (price - baseCost);
    
    if (profit > maxProfit) {
      maxProfit = profit;
      optimalPrice = price;
    }
  }
  
  return {
    price: optimalPrice.toFixed(2),
    profit: maxProfit.toFixed(0),
    volumeChangePercent: getVolumeChange(optimalPrice - priceRange.competitor, elasticityFactor, competitionIntensity)
  };
}

const PriceElasticity = ({ station }) => {
  // Price elasticity
  const [dieselPrice, setDieselPrice] = useState(31.59);
  const [competitorDiesel, setCompetitorDiesel] = useState(31.49);
  const [gasohol95Price, setGasohol95Price] = useState(37.59);
  const [competitorGasohol95, setCompetitorGasohol95] = useState(37.49);
  
  // Enhanced configuration
  const [elasticityFactor, setElasticityFactor] = useState(1.0);
  const [competitionIntensity, setCompetitionIntensity] = useState(1.0);
  const [baselineVolume, setBaselineVolume] = useState(10000);
  const [dieselCost, setDieselCost] = useState(30.0);
  const [modelView, setModelView] = useState('simple'); // 'simple', 'advanced', 'historical'
  const [selectedFuel, setSelectedFuel] = useState('diesel'); // 'diesel' or 'gasohol95'
  
  // Cross-price elasticity effects
  const crossPriceEffect = useMemo(
    () => getCrossPriceEffect(dieselPrice - competitorDiesel),
    [dieselPrice, competitorDiesel]
  );

  // Generate historical price-volume data points
  const historicalPriceVolumeData = useMemo(() => generatePriceVolumeDataPoints(), []);

  // Elasticity curve data
  const elasticityData = useMemo(() => {
    const result = [];
    for (let diff = -0.3; diff <= 0.3; diff += 0.05) {
      const dieselVolume = getVolumeChange(diff, elasticityFactor, competitionIntensity);
      const gasohol95Volume = getVolumeChange(diff, elasticityFactor * 0.8, competitionIntensity);  // Gasohol is slightly less elastic
      result.push({ 
        priceDiff: diff.toFixed(2), 
        dieselResponse: dieselVolume,
        gasohol95Response: gasohol95Volume
      });
    }
    return result;
  }, [elasticityFactor, competitionIntensity]);

  // Current price differences and volume impacts
  const currentDieselDiff = useMemo(
    () => dieselPrice - competitorDiesel,
    [dieselPrice, competitorDiesel]
  );
  
  const estimatedDieselImpact = useMemo(
    () => getVolumeChange(currentDieselDiff, elasticityFactor, competitionIntensity),
    [currentDieselDiff, elasticityFactor, competitionIntensity]
  );

  // Calculate predicted volume, margin and profit
  const predictedChange = getVolumeChange(
    dieselPrice - competitorDiesel, 
    elasticityFactor, 
    competitionIntensity
  );
  const predictedVolume = baselineVolume * (1 + predictedChange / 100);
  const marginPerLiter = dieselPrice - dieselCost;
  const predictedMargin = predictedVolume * marginPerLiter;

  // Calculate profit impact
  const profitImpact = useMemo(() => {
    return calculateProfitImpact(
      baselineVolume,
      competitorDiesel,  // Assumes we were initially at competitor price
      dieselPrice,
      dieselCost,
      predictedChange
    );
  }, [baselineVolume, competitorDiesel, dieselPrice, dieselCost, predictedChange]);

  // Find optimal price for maximum profit
  const optimalPrice = useMemo(() => {
    return findOptimalPrice(
      baselineVolume,
      dieselCost,
      { min: 30.99, max: 32.19, competitor: competitorDiesel },
      elasticityFactor,
      competitionIntensity
    );
  }, [baselineVolume, dieselCost, competitorDiesel, elasticityFactor, competitionIntensity]);

  // Generate what-if pricing scenarios
  const pricingScenarios = useMemo(() => {
    const scenarios = [];
    // Scenario 1: Match competitor
    const matchCompetitor = { 
      name: "Match Competitor",
      price: competitorDiesel, 
      volume: baselineVolume,
      margin: competitorDiesel - dieselCost,
      volumeChange: "0.0%"
    };
    matchCompetitor.profit = matchCompetitor.volume * matchCompetitor.margin;
    
    // Scenario 2: Current price
    const currentPrice = {
      name: "Current Price",
      price: dieselPrice,
      volume: predictedVolume,
      margin: marginPerLiter,
      volumeChange: `${predictedChange.toFixed(1)}%`
    };
    currentPrice.profit = currentPrice.volume * currentPrice.margin;
    
    // Scenario 3: Optimal price
    const optimalPriceScenario = {
      name: "Optimal Price",
      price: Number(optimalPrice.price),
      margin: Number(optimalPrice.price) - dieselCost,
      volumeChange: `${optimalPrice.volumeChangePercent.toFixed(1)}%`
    };
    optimalPriceScenario.volume = baselineVolume * (1 + Number(optimalPrice.volumeChangePercent) / 100);
    optimalPriceScenario.profit = optimalPriceScenario.volume * optimalPriceScenario.margin;
    
    // Scenario 4: Premium price
    const premiumPrice = {
      name: "Premium (+0.2฿)",
      price: competitorDiesel + 0.2,
      margin: (competitorDiesel + 0.2) - dieselCost,
      volumeChange: `${getVolumeChange(0.2, elasticityFactor, competitionIntensity).toFixed(1)}%`
    };
    premiumPrice.volume = baselineVolume * (1 + getVolumeChange(0.2, elasticityFactor, competitionIntensity) / 100);
    premiumPrice.profit = premiumPrice.volume * premiumPrice.margin;
    
    scenarios.push(matchCompetitor, currentPrice, optimalPriceScenario, premiumPrice);
    return scenarios;
  }, [baselineVolume, competitorDiesel, dieselPrice, dieselCost, predictedVolume, marginPerLiter, predictedChange, optimalPrice, elasticityFactor, competitionIntensity]);

  // Handler for simulation button
  const handleSimulation = () => {
    // In a real app, this would likely save the scenario or run a more detailed simulation
    alert(`Simulation completed with ${dieselPrice}฿ diesel price, projecting ${Math.round(predictedVolume).toLocaleString()} L volume and ${Math.round(predictedMargin).toLocaleString()} ฿ margin. Optimal pricing would be ${optimalPrice.price}฿.`);
  };

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Advanced Price Elasticity Analysis & Simulator
      </h3>

      {/* Model Selection Tabs */}
      <div className="flex space-x-1 mb-4">
        <button
          onClick={() => setModelView('simple')}
          className={`px-4 py-2 text-sm font-medium rounded-md ${
            modelView === 'simple'
              ? 'bg-purple-700 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Basic Model
        </button>
        <button
          onClick={() => setModelView('advanced')}
          className={`px-4 py-2 text-sm font-medium rounded-md ${
            modelView === 'advanced'
              ? 'bg-purple-700 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Advanced Model
        </button>
        <button
          onClick={() => setModelView('historical')}
          className={`px-4 py-2 text-sm font-medium rounded-md ${
            modelView === 'historical'
              ? 'bg-purple-700 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Historical Analysis
        </button>
      </div>

      {/* Fuel Type Selection */}
      <div className="flex space-x-2 mb-6">
        <button
          onClick={() => setSelectedFuel('diesel')}
          className={`px-3 py-1 text-sm font-medium rounded-md ${
            selectedFuel === 'diesel'
              ? 'bg-purple-700 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Diesel
        </button>
        <button
          onClick={() => setSelectedFuel('gasohol95')}
          className={`px-3 py-1 text-sm font-medium rounded-md ${
            selectedFuel === 'gasohol95'
              ? 'bg-purple-700 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Gasohol 95
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        {/* Left Column */}
        <div className="lg:col-span-8 space-y-6">
          {/* Chart Section */}
          {modelView === 'simple' && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 h-80">
              <h4 className="text-md font-medium text-gray-800 mb-3 flex items-center">
                <TrendingUp className="w-4 h-4 mr-2 text-purple-600" />
                Price Elasticity Curve
              </h4>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={elasticityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis 
                    dataKey="priceDiff" 
                    label={{ value: 'Price Diff (฿)', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis 
                    domain={[-20, 5]} 
                    label={{ value: 'Volume Impact (%)', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip />
                  <Legend />
                  <ReferenceLine x="0" stroke="#777" strokeDasharray="3 3" />
                  <ReferenceLine y="0" stroke="#777" strokeDasharray="3 3" />
                  <Line
                    type="monotone"
                    dataKey={selectedFuel === 'diesel' ? 'dieselResponse' : 'gasohol95Response'}
                    stroke="#A100FF"
                    strokeWidth={2}
                    dot={{ r: 4, fill: '#A100FF' }}
                    name={`${selectedFuel === 'diesel' ? 'Diesel' : 'Gasohol 95'} Volume Change`}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {modelView === 'advanced' && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 h-80">
              <h4 className="text-md font-medium text-gray-800 mb-3 flex items-center">
                <BarChart2 className="w-4 h-4 mr-2 text-purple-600" />
                Profit Optimization Analysis
              </h4>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={pricingScenarios}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                  <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                  <Tooltip formatter={(value, name) => [
                    name === 'profit' ? `${Number(value).toLocaleString()} ฿` : 
                    name === 'volume' ? `${Number(value).toLocaleString()} L` : 
                    value
                  ]} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="volume" fill="#8884d8" name="Volume (L)" />
                  <Bar yAxisId="right" dataKey="profit" fill="#82ca9d" name="Profit (฿)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {modelView === 'historical' && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 h-80">
              <h4 className="text-md font-medium text-gray-800 mb-3 flex items-center">
                <Clipboard className="w-4 h-4 mr-2 text-purple-600" />
                Historical Price-Volume Relationship
              </h4>
              <ResponsiveContainer width="100%" height="90%">
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    type="number" 
                    dataKey="price" 
                    name="Price" 
                    label={{ value: 'Price (฿)', position: 'insideBottom', offset: -5 }} 
                    domain={['dataMin', 'dataMax']} 
                  />
                  <YAxis 
                    type="number" 
                    dataKey="volume" 
                    name="Volume" 
                    label={{ value: 'Volume (L)', angle: -90, position: 'insideLeft' }} 
                  />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(value) => Number(value).toLocaleString()} />
                  <Scatter name="Price-Volume Data Points" data={historicalPriceVolumeData} fill="#8884d8" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          )}
          
          {/* Pricing Scenario Comparison */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-800 mb-3 flex items-center">
              <DollarSign className="w-4 h-4 mr-2 text-purple-600" />
              Pricing Scenario Comparison
            </h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scenario
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price (฿)
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Volume Change
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Volume (L)
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Margin (฿/L)
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Profit (฿)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pricingScenarios.map((scenario, index) => (
                    <tr key={index} className={scenario.name === "Optimal Price" ? "bg-purple-50" : ""}>
                      <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                        {scenario.name} {scenario.name === "Optimal Price" && "✨"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                        {scenario.price.toFixed(2)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                        {scenario.volumeChange}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                        {Math.round(scenario.volume).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                        {scenario.margin.toFixed(2)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                        {Math.round(scenario.profit).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-4 space-y-6">
          {/* Simulation Controls */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-md font-medium text-gray-800 mb-3 flex items-center">
              <Calculator className="w-4 h-4 mr-2 text-purple-600" />
              Price Simulation Controls
            </h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Our {selectedFuel === 'diesel' ? 'Diesel' : 'Gasohol 95'} Price: {(selectedFuel === 'diesel' ? dieselPrice : gasohol95Price).toFixed(2)} ฿
                </label>
                <input
                  type="range"
                  min={selectedFuel === 'diesel' ? "30.99" : "36.99"}
                  max={selectedFuel === 'diesel' ? "32.19" : "38.19"}
                  step="0.10"
                  value={selectedFuel === 'diesel' ? dieselPrice : gasohol95Price}
                  onChange={(e) => selectedFuel === 'diesel' ? 
                    setDieselPrice(Number(e.target.value)) : 
                    setGasohol95Price(Number(e.target.value))
                  }
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Competitor {selectedFuel === 'diesel' ? 'Diesel' : 'Gasohol 95'} Price: {(selectedFuel === 'diesel' ? competitorDiesel : competitorGasohol95).toFixed(2)} ฿
                </label>
                <input
                  type="range"
                  min={selectedFuel === 'diesel' ? "30.59" : "36.59"}
                  max={selectedFuel === 'diesel' ? "31.99" : "37.99"}
                  step="0.10"
                  value={selectedFuel === 'diesel' ? competitorDiesel : competitorGasohol95}
                  onChange={(e) => selectedFuel === 'diesel' ? 
                    setCompetitorDiesel(Number(e.target.value)) : 
                    setCompetitorGasohol95(Number(e.target.value))
                  }
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                />
              </div>

              {modelView === 'advanced' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Baseline Volume: {baselineVolume.toLocaleString()} L
                    </label>
                    <input
                      type="range"
                      min="5000"
                      max="20000"
                      step="1000"
                      value={baselineVolume}
                      onChange={(e) => setBaselineVolume(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Cost: {dieselCost.toFixed(2)} ฿
                    </label>
                    <input
                      type="range"
                      min="29.0"
                      max="31.0"
                      step="0.10"
                      value={dieselCost}
                      onChange={(e) => setDieselCost(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Elasticity Factor: {elasticityFactor.toFixed(1)}x
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="1.5"
                      step="0.1"
                      value={elasticityFactor}
                      onChange={(e) => setElasticityFactor(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Higher values mean consumers are more sensitive to price changes
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Competition Intensity: {competitionIntensity.toFixed(1)}x
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="1.5"
                      step="0.1"
                      value={competitionIntensity}
                      onChange={(e) => setCompetitionIntensity(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Higher values mean stronger competitive effects in the market
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Results Section */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Simulation Results</h5>
              <div className="mb-2 text-sm">
                <strong>Price Difference:</strong> {(selectedFuel === 'diesel' ? currentDieselDiff : gasohol95Price - competitorGasohol95).toFixed(2)} ฿
              </div>
              <div className="mb-2 text-sm">
                <strong>Predicted Volume Change:</strong> {(selectedFuel === 'diesel' ? estimatedDieselImpact : getCrossPriceEffect(currentDieselDiff)).toFixed(1)}%
              </div>
              <div className="mb-2 text-sm">
                <strong>Predicted Daily Volume:</strong> {Math.round(predictedVolume).toLocaleString()} L
              </div>
              <div className="mb-2 text-sm">
                <strong>Estimated Daily Margin:</strong> {Math.round(predictedMargin).toLocaleString()} ฿
              </div>
              {modelView === 'advanced' && (
                <>
                  <div className="mb-2 text-sm">
                    <strong>Profit Change:</strong>{' '}
                    <span className={profitImpact.profitPercent >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {profitImpact.profitPercent >= 0 ? '+' : ''}
                      {profitImpact.profitPercent.toFixed(1)}%
                    </span>
                  </div>
                  <div className="mb-2 text-sm">
                    <strong>Cross-Price Effect on Gasohol:</strong>{' '}
                    <span className={crossPriceEffect >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {crossPriceEffect >= 0 ? '+' : ''}
                      {crossPriceEffect.toFixed(1)}%
                    </span>
                  </div>
                  <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded text-sm">
                    <strong className="text-purple-700">Optimal Price:</strong> {optimalPrice.price} ฿
                    <br />
                    <span className="text-gray-600">
                      Generates {Math.round(Number(optimalPrice.profit)).toLocaleString()} ฿ in profit
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Alert for extreme values */}
            {Math.abs(estimatedDieselImpact) > 10 && (
              <div className="mt-4 p-2 bg-yellow-50 border border-yellow-300 rounded flex items-start">
                <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                <span className="text-xs text-yellow-800">
                  Caution: The projected volume change is significant. Consider a less aggressive pricing strategy.
                </span>
              </div>
            )}

            <button 
              onClick={handleSimulation}
              className="w-full mt-4 bg-purple-700 text-white py-2 px-4 rounded-md hover:bg-purple-800 transition-colors"
            >
              Simulate Strategy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceElasticity;
